// Angular
import { ChangeDetectorRef, Component, ElementRef, Injector, Input, Renderer2, WritableSignal, computed, effect, inject, signal } from "@angular/core";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { Location } from '@angular/common'; // <-- Must have this otherwise the NodeJs version will be imported and that's wrong.

// EZ
import { DocMeta } from "@template-ui/decorators";
import { toggle } from "@template-ui/helpers";
import { EzRoutingSignalsMixin } from "@template-ui/mixins/ez-routing-signals.mixin";
import { ExcelService } from "@template-ui/services";

import { LoadingService } from "../services/loading.service";
import { RoleService } from "../services/roles.service";
import { UserService } from "../services/user.service";
// import { OidcSecurityService } from "angular-auth-oidc-client";
import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";
import { AppAngularBaseClass } from "@app/core/services/app-angular-base";
import Prism from "prismjs";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { SettingsService } from "../services/settings.service";
import FileSaver from "file-saver";

/**
 * ## App Routing Base Component
 * Abstract base class for app components that need routing signals. Extends `EzBaseComponent` and
 * provides a unified, reactive foundation for working with app APIs, route
 * parameters, shared services, and common UI behaviors.
 *
 * ## Purpose
 * `AppRoutingBaseComponent` centralizes app-specific functionality so that feature
 * components can focus on their domain logic. It provides:
 *
 * - Reactive access to well‑known route parameters (`cdcNum`, `id`, `planId`)
 * - Access to all app API endpoint helpers via `this.apis`
 * - Shared app services (roles, loading, user, Excel, OIDC, config, state)
 * - Automatic UI resets on navigation (summary bar, header, etc.)
 * - A global developer message signal for lightweight debugging
 *
 * ## Route Parameter Signals
 * The following signals are exposed for use inside `effect()` blocks:
 *
 * - `cdcNumSignal` — string or undefined
 * - `idSignal` — string or undefined (merged from route or `@Input()`)
 * - `planIdSignal` — number
 *
 * These signals update reactively as the route changes. Components should
 * subscribe to them inside an `effect()` to safely load data:
 *
 * ```ts
 * effect(() => {
 *   if (this.idSignal()) {
 *     this.loadData();
 *   }
 * });
 * ```
 *
 * ## Inputs
 * Components may override route parameters by binding:
 *
 * - `@Input() cdcNum`
 * - `@Input() safeId`
 *
 * These values merge seamlessly with route‑derived values through the
 * corresponding signals.
 *
 * ## Navigation Reset Behavior
 * On each navigation start, the component automatically:
 *
 * - Collapses the common header
 * - Collapses the case summary bar
 * - Resets summary-related state in `AppStateService`
 *
 * This ensures consistent UI behavior across all app screens.
 *
 * ## Developer Messaging
 * The static `devMessageSig` provides a lightweight way to display temporary
 * messages in the UI footer during development:
 *
 * ```ts
 * this.devMessage("Loaded case data");
 * ```
 *
 * Messages auto‑clear after a timeout.
 *
 * ## Usage
 * Extend this class in any app component:
 *
 * ```ts
 * @Component({...})
 * export class CaseDetailComponent extends AppRoutingBaseComponent {
 *   constructor() {
 *     super();
 *     effect(() => {
 *       if (this.cdcNumSignal()) {
 *         this.loadCase();
 *       }
 *     });
 *   }
 * }
 * ```
 *
 * This class should not be instantiated directly.
 */
@Component({
    selector: "ui-base, ez-base",
    imports: [RouterModule],
    template: ` <p>I am a base component, extend me!</p> `,
    styles: []
})
@DocMeta({
  title: "Base",
  category: "Components",
  //showcaseComponentName: 'EzSectionSampleComponent'
})
export abstract class AppRoutingBaseComponent extends EzRoutingSignalsMixin(AppAngularBaseClass) {

  // This is the same stuff that's in the EzBaseComponent but we don't extend that anymore so it's repeated here
  /**
   * Prism is a syntax highlighter and works well with the markdown library.
   *
   * Expose the Prism library to child components so
   * they don't have to re-import Prism. Any child component that has a code block
   * with a language class will automatically get highlighing in ngAfterViewInit
   * @example
   * ```html
   * <pre><code class="language-ts"
   *  class MyClass {
   *    ImClassy() {
   *      return 'Im a classy func!';
   *    }
   *  }
   * </code></pre>
   * ```
   *
   * Child component can also access the Prism functions by using
   * ```ts
   *  this.Prism.highlightAll();
   * ```
   */
  protected readonly Prism = Prism;

  protected override injector = inject(Injector);
  protected sanitizer: DomSanitizer = inject(DomSanitizer);
  protected cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected el: ElementRef = inject(ElementRef);
  protected renderer: Renderer2 = inject(Renderer2);
  protected location = inject(Location);

  protected settings = inject(SettingsService);

  /**
   * ## App APIs
   *
   * All the app APIs in one place
   * This is a conveniece class to make it easy to find api endpoints.
   * In your component, if you need an endpoint, do this:
   * ```ts
   *   let myEndpoint = this.apis.core.getAppUserSettings();
   * ```
   * If your endpoint uses path parameters, enter them in the function call like this:
   * ```ts
   *   let moduleTyc = "SOME_CODE";
   *   myEndpoint = this.apis.core.getActivityLogTypeByModuleTyc(moduleTyc);
   * ```
   * The result will be a complete URL starting with ```http``` that can be used in the crudService (or with http.get/post etc.)
   */
  //readonly apis = inject(AppApis);

  // Get the app well-known query parameters as signals for use in components

  private _cdcNumSig = signal<string | undefined>(undefined);
  /**
   * The cdcNum as a signal from the query params
   */
  protected readonly cdcNumSignal = computed(() => this.activatedRouteParamsSignal()?.get("cdcNum") ?? this._cdcNumSig());

  private _idSig = signal<string>("");
  /* *
  * The id as a signal from the query params. This could be caseId or some other id that doesn't have a more specific name.
  * This also allows the id to be passed from a parent component
  */
  // Moved to app state service - should probably do the same for cdcNum, planId etc.
  // protected readonly idSignal = computed(() => {
  //   const fromRoute = this.activatedRouteParamsSignal()?.get("id");
  //   const fromInput = this._idSig();

  //   return fromInput || fromRoute || undefined;
  // });



  private _planIdSig = signal<number>(-1);
  /**
   * The planId as a signal from the query params. It is always a number.
   */
  protected readonly planIdSignal = computed(() => Number(this.activatedRouteParamsSignal()?.get("planId") ?? this._planIdSig()));

  // App services commonly used in components
  protected readonly userService = inject(UserService);
  protected readonly loading = inject(LoadingService);
  protected readonly excel = inject(ExcelService);
  protected readonly roles = inject(RoleService);
  // protected readonly oidcService: OidcSecurityService = inject(OidcSecurityService);
  protected readonly appConfigService = inject(ApplicationConfigurationService);
  //protected readonly appState = inject(AppStateService);
  protected readonly appConfig = this.appConfigService.appConfig;

  protected readonly idSignal = this.appState.idSignal;

  // public setBusy(state: boolean): void {
  //   //this._isBusy = state;
  //   //this.appConfigService.isBusySignal.set(state);
  //   this.isBusy.set(state);
  // }


  /**
   * This is commonly input into components but will also be extracted from the URL if it exists.
   * Is comes from a signal so it can (and should be) used within effect().
   *
   * This will return the query param specified as .../{cdcNum}/.. or ?cdcNum=\<cdcNum>.
   *
   * To ensure this exists from the query params before using it, put whatever code needs it inside
   * an effect like this:
   * ```ts
   * constructor()
   * {
   *   super();
   *   effect(() => {
   *     if(this.cdcNum)
   *     {
   *       this.loadData();  // can safely use this.cdcNum (any any other query params like id)
   *     }
   *   });
   * }
   * ```
   * If this is passed via the @ Input(), then don't use until either ngOnChanges or ngOnInit.
   *
   * effect() will also work.
   */
  @Input()
  set cdcNum(value: string | undefined) {
    //this.cdcNumSignal.set(value);
    this._cdcNumSig.set(value || "");
    //console.log(`Got cdcNum in base for ${this.constructor.name}: ${value}`);
  }

  get cdcNum(): string | undefined {
    return this.cdcNumSignal() || undefined;
  }

  /**
  *  ## NOTE
   * This is called 'safeId' only because 'id' is alredy being mis-used all over the place.
   *
   * A queryparam from snapshot is not necessarily available in the constructor.
   *
   * IF YOU ARE TOUCHING CODE THAT HAS ITS OWN id QUERY PARAM, THEN MIGRATE IT TO safeId.
   *
   * Once they're all done, we can rename safeId to just id.
   *
   * This is commonly input into components but will also be extracted from the URL if it exists.
   *
   * The id is specific to the route, it could represent a caseId, reviewId etc.
   *
   * This will return the query param specified as .../:id/.. or ?id=\<id>.
   *
   * To ensure this exists from the query params before using it, put whatever code needs it inside
   *
   * an effect like this:
   * ```ts
    constructor()
    {
      super();

      effect(() => {
        if(this.id)
        {
          this.caseId = Number(this.id);   // id is a caseId and is a number
          this.loadData();                 // can safely use this.caseId (any any other query params like cdcNum)
        }
      });
    }
   * ```
   * If this is passed via the @ Input(), then don't use until either ngOnChanges or ngOnInit.
   *
   * or just use effect().

   TODO:
   How to migrate:
   - For new components, do not try to get id from the query params, just use this.safeId.
   - For existing components, if you're messing with it already, convert id to safeId.
   - When all the individual ids are gone, we can refactor to make this just id.
  */
  @Input()
  set safeId(value: string) {
    this._idSig.set(value);
  }

  /**
   * @deprecated - just use this.idSignal()
   */
  get safeId(): string | undefined {
    return this.idSignal ? this.idSignal() : undefined;
  }


  /**
   * cdcNum and id will be available
   * in the child component in an effect.
   */
  private getCommonParamsFromRoute(): void {

    const allParams: any = {};
    let currentRoute: ActivatedRoute | null = this.activatedRoute;
    // Walk up the tree to collect params (both path and query) from parent routes
    while (currentRoute) {
      Object.assign(allParams, currentRoute.snapshot.params);        // Path params. I.e. .../:cdcNum/...
      Object.assign(allParams, currentRoute.snapshot.queryParams);   // Query params. I.e. ../..?cdcNum=THX1138
      currentRoute = currentRoute.parent;
    }

    // Get the cdcNum
    const cdcNum = allParams['cdcNum'];
    if (cdcNum) {
      this.cdcNum = cdcNum;  // This will fire the signal
      this.dlog(`Got cdcNum: ${this.cdcNum}`);
    }

    // Get the id param but called safeId in here.
    const safeId = allParams['id'];
    if (safeId) {
      this.safeId = safeId;  // This will fire the signal
      this.dlog(`Got safeId: ${this.safeId}`);

      this.appState.idSignal.set(safeId);
    }

    // Get the plan id param but called safeId in here.
    // const planId = allParams['planId'];
    // if (planId) {
    //   this.safePlanId = planId;  // This will fire the signal
    //  // this.safePlanId = planId;  // This will fire the signal
    //   this.dlog(`Got planId: ${this.safePlanId}`);
    // }
  }

  // NOTE: This could be done with a service but do we need a whole service for just one signal?
  // TODO: Now we have the app state service, does it belong there?
  static devMessageSig = signal("");
  private _devMsgTimeout: ReturnType<typeof setTimeout> | null = null;

  // The signal lives on the static base component (i.e it's everywhere) so I
  // didn't have to create a service. Maybe in the future it'll go in a service.
  devMessageSig = computed(() => AppRoutingBaseComponent.devMessageSig());

  /**
   * See a message in the footer while developing. This is easier to see than a console
   * message and less obnoxious than an alert.
   */
  devMessage(msg: string, timeoutInMs: number = 3000) {
    if (!this.roles.isDeveloper()) return;

    // Clear previous message timeout
    if (this._devMsgTimeout) {
      clearTimeout(this._devMsgTimeout);
      this._devMsgTimeout = null;
    }

    // Done in a requestAnimationFrame so it's safe to run devMessage from in an effect.
    requestAnimationFrame(() => {
      AppRoutingBaseComponent.devMessageSig.set(`<span class="opacity-50">Dev msg: </span>${msg}`);
    });

    // Schedule message cleanup
    this._devMsgTimeout = setTimeout(() => {
      AppRoutingBaseComponent.devMessageSig.set("");
      this._devMsgTimeout = null;
    }, timeoutInMs);
  }

  private stripFragment(url: string): string {
    const idx = url.indexOf('#');
    return idx === -1 ? url : url.slice(0, idx);
  }

  private _lastUrl = '';
  constructor() {
    super();

    effect(() => {
      // Reset any signals that should be reset upon navigation
      const nav = this.navigationStartSignal();
      if (!nav) return;

      const current = this.stripFragment(nav.url);
      const previous = this._lastUrl;

      this.dlog(`Prev url: ${previous}, current url: ${current}`);

      // Only fire if the path changed
      if (!current.includes("not-found") && !!previous && current !== previous) {

        this.dlog(`About to hide stuff`);
        this.appState.showCaseSummaryBar.set(false);
        this.appState.commonHeaderExpanded.set(false);
        this.appState.summaryBarExpanded.set(false);
      }
      if (!current.includes("not-found")) {
        this._lastUrl = current;
      }

      // // If a particular detail screen should show the case summary bar, then that screen should set this to true
      // this.appState.showCaseSummaryBar.set(false);

      // // If the common header was expanded, close it
      // this.appState.commonHeaderExpanded.set(false);

      // // If the case summary bar was expanded, close it
      // this.appState.summaryBarExpanded.set(false);

    }, { allowSignalWrites: true });

    effect(() => {
      const nav = this.navigationEndSignal();
      if (!nav) return;

      this.getCommonParamsFromRoute();
    }, { allowSignalWrites: true });

    // TODO: This should not be necessary as the route params are not guaranteed until navigation
    // end which is happening above. That said, stuff breaks if you take this out. Figure out why.
    this.getCommonParamsFromRoute();
  }

  /**
   * This doesn't do anything yet except require sub-classes to override ngAfterView Init and
   * call super.ngAfterViewInit();
   * or override so that in the future, if we want something here, we won't
   * have to go refactor everything.
   * Here's a starter
   * ```ts
   *  override ngAfterViewInit(): void {
   *     super.ngAfterViewInit();
   *     // Do your stuff
   *  }
   * ```
   */
  ngAfterViewInit(): void {

    // If the component contains any <code> block with prism classes, like "language-html", then perform highlighting.
    // If you need other languages, import them above.
    this.Prism.highlightAll();
  }

  /**
   * Returns a trusted URL for displaying a PDF.
   * @param arrayBuffer - usually comes from an API call that returns a blob
   * @example
   * ```ts
   *   this.crudService.GetFile(url).pipe(
           map((response) => {
             this.trustedUrl = this.getTrustedUrlForPdf(response);
           }),
           this.takeUntilDestroyed()
         ).subscribe();
   *  ...
   * ```
   * @returns trusted URL for PDF display
   */
  getTrustedUrlForPdf(arrayBuffer: any): SafeResourceUrl {
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Download file as a PDF.
   * @param buffer - usually comes from an API call that returns a blob
   * @example
   * ```ts
   *   this.crudService.GetFile(url).pipe(
         map((response) => {
           this.saveAsPdfFile(response, `InvestigationReport_${investigationId}`);
         }),
         this.takeUntilDestroyed()
       ).subscribe();
   *  ...
   * ```
   * @returns void
   */
  saveAsPdfFile(buffer: BlobPart, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: "application/pdf",
    });
    FileSaver.saveAs(data, fileName + ".pdf");
  }

  /**
   * Toggle a boolean signal - repeated here to make
   * available in templates.
   * @param booleanSig The boolean signal to be toggled
   */
  toggle(booleanSig: WritableSignal<boolean>) {
    toggle(booleanSig);
  }
}
